import { Dialog, DialogContent, DialogDescription, DialogTitle } from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { User } from 'types/common_types';
import { Formik } from 'formik';
import { user_schema, user_validation_schema } from 'validation_schema/user_validation_schema';
import ErrorText from 'components/ErrorText';
import Text from 'components/Text';
import { fontSizes } from 'appconstants';
import InputField from 'components/InputField';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import { createUser, updateUser } from 'store/actions/user-action';
import { admin_role_id } from 'config';

interface Props {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  selectedUser?: User | null;
  onDone?: () => void;
}

const UserModal = ({ open, onClose, mode, selectedUser, onDone }: Props) => {
  const dispatch: AppDispatch = useDispatch();

  const isEdit = mode === 'edit';

  const { user } = useSelector((state: RootState) => state.common);
  const { user_loading } = useSelector((state: RootState) => state.user);

  const onSubmit = (values: any, resetForm: () => void) => {
    const api_data = {
      fullName: values.fullName,
      email: values.email,
      password: values.password,
      ContactNo: values.contactNo,
      roleId: admin_role_id,
    };

    if (isEdit) {
      api_data['userId'] = selectedUser?.userId;
      delete api_data['password'];
      api_data['updatedBy'] = user?.userId;
      api_data['notification'] = user?.notification;
      api_data['isActive'] = true;
    } else {
      api_data['createdBy'] = user?.userId;
    }

    if (isEdit) {
      dispatch(
        updateUser(api_data, () => {
          resetForm();
          onClose();
          onDone && onDone();
        }),
      );
    } else {
      dispatch(
        createUser(api_data, () => {
          resetForm();
          onClose();
          onDone && onDone();
        }),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={user_loading ? null : onClose}>
      <DialogContent className='max-w-[720px] rounded-xl'>
        <DialogTitle className='hidden'></DialogTitle>
        <DialogDescription className='hidden'></DialogDescription>
        <Text text={!isEdit ? 'Create User' : 'Edit User'} className='font-bold' size={fontSizes.h20} />
        <Text text={`Fill in the details ${isEdit ? "edit an existing" : "to create a new"} User.`} className='text-muted-foreground mt-3' size={fontSizes.h14} />

        <Formik
          initialValues={selectedUser ?? user_schema}
          validationSchema={user_validation_schema}
          validateOnBlur={false}
          validateOnChange={false}
          onSubmit={(values, functions) => {
            onSubmit(values, functions.resetForm);
          }}
        >
          {({ values, errors, setFieldValue, setFieldError, handleSubmit, resetForm }) => (
            <>
              <div className='grid grid-cols-1 gap-4'>
                <div>
                  <InputField
                    label='Name'
                    placeholder='Enter Name'
                    value={values.fullName}
                    onChange={(text) => {
                      setFieldValue('fullName', text);
                      setFieldError('fullName', '');
                    }}
                  />
                  {errors.fullName && <ErrorText text={errors.fullName} />}
                </div>

                <div>
                  <InputField
                    label='Email'
                    placeholder='Enter Email'
                    value={values.email}
                    props={{
                      autoComplete: 'off',
                      name: 'email',
                    }}
                    onChange={(text) => {
                      setFieldValue('email', text);
                      setFieldError('email', '');
                    }}
                  />
                  {errors.email && <ErrorText text={errors.email} />}
                </div>

                <div>
                  <InputField
                    label='Contact No'
                    placeholder='Enter Contact No'
                    value={values.contactNo}
                    props={{
                      autoComplete: 'off',
                      name: 'contactNo',
                      maxLength: 15,
                      inputMode: 'numeric',
                    }}
                    onChange={(text) => {
                      setFieldValue('contactNo', text);
                      setFieldError('contactNo', '');
                    }}
                  />
                </div>

                {!isEdit && (
                  <div>
                    <InputField
                      label='Password'
                      type='password'
                      placeholder='Enter Password'
                      props={{
                        autoComplete: 'new-password',
                        name: 'new-password',
                      }}
                      value={values.password}
                      onChange={(text) => {
                        setFieldValue('password', text);
                        setFieldError('password', '');
                      }}
                    />
                    {errors.password && <ErrorText text={errors.password} />}
                  </div>
                )}
              </div>

              <div className='mt-10 flex justify-between gap-4'>
                <Button
                  variant='outline'
                  className='h-12 w-full rounded-xl border-primary text-primary'
                  onClick={() => {
                    resetForm();
                    onClose();
                  }}
                >
                  Cancel
                </Button>

                <Button className='h-12 w-full rounded-xl' onClick={() => handleSubmit()} loading={user_loading}>
                  {!isEdit ? 'Create' : 'Save'}
                </Button>
              </div>
            </>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default UserModal;
